import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import Icon from '../../../../../../atoms/Icon'
import RequestRow from '../../shared/RequestRow'
import LogRow from '../../shared/LogRow'
import SharedRequestDetailModal from '../../shared/RequestDetailModal'

import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/theme-xcode'

const TABS = [
  { id: 'source', label: 'Source' },
  { id: 'result', label: 'Result' },
  { id: 'requests', label: 'Requests' },
  { id: 'logs', label: 'Logs' }
]

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, var(--color-success-500), var(--color-success-700))',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      transform: 'scale(1.15)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
      filter: 'brightness(1.1)'
    },
    '&:active': {
      transform: 'scale(1.04)'
    }
  },
  chipError: {
    background: 'linear-gradient(135deg, var(--color-danger-500), var(--color-danger-700))'
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius-lg)',
    width: '75vw',
    maxWidth: '900px',
    height: '75vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--color-neutral-900)',
    flexShrink: 0
  },
  modeBadge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: 'var(--font-mono, monospace)',
    flexShrink: 0
  },
  queryBadge: {
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)'
  },
  commandBadge: {
    backgroundColor: 'var(--color-warning-050)',
    color: 'var(--color-warning-700)'
  },
  summaryText: {
    fontSize: '12px',
    color: 'var(--color-neutral-500)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    padding: '4px',
    flexShrink: 0,
    '&:hover': {
      color: 'var(--color-neutral-600)'
    }
  },
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0
  },
  bannerPass: {
    backgroundColor: 'var(--color-success-050)',
    color: 'var(--color-success-700)',
    borderBottom: '1px solid var(--color-success-200)'
  },
  bannerFail: {
    backgroundColor: 'var(--color-danger-050)',
    color: 'var(--color-danger-700)',
    borderBottom: '1px solid var(--color-danger-200)'
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  tab: {
    padding: '8px 16px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    borderBottom: '2px solid transparent',
    '&:hover': {
      color: 'var(--color-neutral-800)'
    }
  },
  tabActive: {
    color: 'var(--color-primary-700)',
    borderBottomColor: 'var(--color-primary-500)',
    fontWeight: 600
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '14px 20px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  errorBox: {
    padding: '8px 12px',
    marginBottom: '10px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--color-danger-700)',
    backgroundColor: 'var(--color-danger-050)',
    border: '1px solid var(--color-danger-200)',
    lineHeight: 1.5
  },
  resultPre: {
    margin: 0,
    padding: '10px 12px',
    fontSize: '12px',
    fontFamily: 'var(--font-mono, monospace)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: 'var(--color-neutral-700)',
    lineHeight: 1.5,
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-sm)',
    border: '1px solid var(--color-neutral-200)',
    maxHeight: '100%',
    overflow: 'auto'
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: '13px'
  },
  sourceWrapper: {
    flex: 1,
    overflow: 'hidden',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    '& .ace_editor': {
      fontFamily: 'var(--font-mono, monospace) !important'
    }
  }
}))

function parseEnvelope (resultContent) {
  if (!resultContent) return null
  try {
    const parsed = typeof resultContent === 'string' ? JSON.parse(resultContent) : resultContent
    if (parsed.success && parsed.envelope) {
      const env = parsed.envelope
      const httpEvents = (env.events || []).filter(e => e.type === 'http')
      const logEvents = (env.events || []).filter(e => e.type === 'log')
      return {
        pass: !env.error && !env.assertionError,
        executionMs: env.executionMs || 0,
        result: env.result,
        error: env.error || null,
        assertionError: env.assertionError || null,
        requests: httpEvents.map(e => ({
          method: e.request?.method || 'GET',
          url: e.request?.url || '',
          status: e.response?.status || 0,
          durationMs: e.durationMs,
          mocked: !!e.mocked,
          requestBody: e.request?.body,
          responseBody: e.response?.body
        })),
        logs: logEvents.map(e => ({
          level: e.level || 'info',
          message: e.message || ''
        }))
      }
    }
    if (parsed.success === false) {
      return {
        pass: false,
        executionMs: 0,
        result: null,
        error: typeof parsed.error === 'string'
          ? { message: parsed.error }
          : parsed.error || { message: 'Execution failed' },
        assertionError: null,
        requests: [],
        logs: []
      }
    }
    return null
  } catch {
    return null
  }
}

function ExecutionChip ({ args, resultContent }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('source')
  const [inspectedRequest, setInspectedRequest] = useState(null)

  const summary = args?.summary || args?.mode || 'Execution'
  const hoverText = args?.description || summary
  const mode = args?.mode || 'query'
  const envelope = useMemo(() => parseEnvelope(resultContent), [resultContent])
  const isError = envelope ? !envelope.pass : !resultContent

  return (
    <>
      <span
        className={`${classes.chip} ${isError ? classes.chipError : ''}`}
        title={hoverText}
        onClick={() => setOpen(true)}
      >
        <Icon name='bolt' customSize='15px' color='white' />
      </span>
      {open && (
        <div
          className={classes.overlay}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
        >
          <div className={classes.modal}>
            <div className={classes.header}>
              <div className={classes.headerLeft}>
                <Icon name='bolt' customSize='16px' />
                <span className={classes.headerTitle}>Execution</span>
                <span className={`${classes.modeBadge} ${mode === 'command' ? classes.commandBadge : classes.queryBadge}`}>
                  {mode}
                </span>
                {args?.summary && (
                  <span className={classes.summaryText}>{args.summary}</span>
                )}
              </div>
              <button
                className={classes.closeBtn}
                onClick={() => setOpen(false)}
                aria-label='Close execution detail'
              >
                &times;
              </button>
            </div>

            {envelope && (
              <div className={`${classes.banner} ${envelope.pass ? classes.bannerPass : classes.bannerFail}`}>
                <span>{envelope.pass ? '\u2713 Passed' : '\u2717 Failed'}</span>
                <span style={{ fontWeight: 400 }}>&mdash; {envelope.executionMs}ms</span>
              </div>
            )}

            <div className={classes.tabBar}>
              {TABS.map(tab => (
                <div
                  key={tab.id}
                  className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {tab.id === 'requests' && envelope?.requests && ` (${envelope.requests.length})`}
                  {tab.id === 'logs' && envelope?.logs && ` (${envelope.logs.length})`}
                </div>
              ))}
            </div>

            <div className={classes.body}>
              {!envelope && activeTab !== 'source' && (
                <div className={classes.emptyState}>
                  {resultContent ? 'Could not parse execution result.' : 'Execution still in progress\u2026'}
                </div>
              )}

              {activeTab === 'source' && (
                args?.source
                  ? (
                    <div className={classes.sourceWrapper}>
                      <AceEditor
                        mode='javascript'
                        theme='xcode'
                        value={args.source}
                        readOnly
                        width='100%'
                        height='100%'
                        fontSize={12}
                        showPrintMargin={false}
                        showGutter
                        highlightActiveLine={false}
                        setOptions={{
                          useWorker: false,
                          showLineNumbers: true,
                          tabSize: 2,
                          wrap: true
                        }}
                        editorProps={{ $blockScrolling: true }}
                      />
                    </div>
                  )
                  : <div className={classes.emptyState}>No source code available.</div>
              )}

              {envelope && activeTab === 'result' && (
                <>
                  {envelope.error && (
                    <div className={classes.errorBox}>
                      <strong>{envelope.error.name || 'Error'}:</strong> {envelope.error.message}
                    </div>
                  )}
                  {envelope.assertionError && (
                    <div className={classes.errorBox}>
                      <strong>Assertion failed:</strong> {envelope.assertionError}
                    </div>
                  )}
                  {envelope.result != null && (
                    <pre className={classes.resultPre}>{JSON.stringify(envelope.result, null, 2)}</pre>
                  )}
                  {envelope.result == null && !envelope.error && (
                    <div className={classes.emptyState}>No return value.</div>
                  )}
                </>
              )}

              {envelope && activeTab === 'requests' && (
                <>
                  {envelope.requests.length === 0 && (
                    <div className={classes.emptyState}>No HTTP requests recorded.</div>
                  )}
                  {envelope.requests.map((req, i) => (
                    <RequestRow key={`req-${i}`} request={req} onClick={() => setInspectedRequest(req)} />
                  ))}
                </>
              )}

              {envelope && activeTab === 'logs' && (
                <>
                  {envelope.logs.length === 0 && (
                    <div className={classes.emptyState}>No logs recorded.</div>
                  )}
                  {envelope.logs.map((log, i) => (
                    <LogRow key={`log-${i}`} log={log} />
                  ))}
                </>
              )}
            </div>
          </div>

          <SharedRequestDetailModal
            request={inspectedRequest}
            onClose={() => setInspectedRequest(null)}
          />
        </div>
      )}
    </>
  )
}

ExecutionChip.propTypes = {
  args: PropTypes.object,
  resultContent: PropTypes.string
}

export default ExecutionChip
